import React, { useState } from 'react';
import Button from '../UI/Button';
import { useAppDispatch, useAppSelector } from '../../hooks/redux';
import { castVote } from '../../store/slices/votingSlice';

interface VotingFormProps {
  projectId: string;
  sessionId: string;
  sessionType: 'open' | 'anonymous';
  onVoteCast?: () => void;
  className?: string;
}

const VotingForm: React.FC<VotingFormProps> = ({
  projectId,
  sessionId,
  sessionType,
  onVoteCast,
  className = '',
}) => {
  const dispatch = useAppDispatch();
  const { casting } = useAppSelector((state) => state.voting);
  const { user } = useAppSelector((state) => state.auth);
  const [selectedOption, setSelectedOption] = useState<'yes' | 'no' | 'abstain' | null>(null);
  const [error, setError] = useState<string | null>(null);

  const isCasting = casting[sessionId] || false;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!selectedOption) {
      setError('Please select an option');
      return;
    }

    // For open voting, user must be authenticated
    if (sessionType === 'open' && !user) {
      setError('You must be logged in to vote in open sessions');
      return;
    }

    try {
      setError(null);
      
      // Get geographic data if available
      const geographicData: any = {};
      
      // Try to get user's location from project or user profile
      // This is a simplified version - you might want to get actual coordinates
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            geographicData.coordinates = {
              lat: position.coords.latitude,
              lng: position.coords.longitude,
            };
          },
          () => {
            // Silently fail if geolocation is denied
          }
        );
      }

      await dispatch(castVote({
        projectId,
        sessionId,
        option: selectedOption,
        geographicData: Object.keys(geographicData).length > 0 ? geographicData : undefined,
      }));

      if (onVoteCast) {
        onVoteCast();
      }
    } catch (err: any) {
      setError(err.response?.data?.error?.message || 'Failed to cast vote');
    }
  };

  return (
    <form onSubmit={handleSubmit} className={`space-y-4 ${className}`}>
      {error && (
        <div className="p-3 bg-[var(--color-error-50)] border border-[var(--color-error-200)] rounded-lg text-[var(--color-error-700)] text-sm">
          {error}
        </div>
      )}

      <div className="space-y-2">
        <label className="block text-sm font-medium text-[var(--color-default-font)] mb-2">
          Your Vote:
        </label>
        <div className="space-y-2">
          <label className="flex items-center p-3 border border-[var(--color-neutral-border)] rounded-lg cursor-pointer hover:bg-[var(--color-neutral-50)] transition-colors bg-[var(--color-default-background)]">
            <input
              type="radio"
              name="vote"
              value="yes"
              checked={selectedOption === 'yes'}
              onChange={() => setSelectedOption('yes')}
              className="mr-3"
              disabled={isCasting}
            />
            <span className="flex-1 font-medium text-[var(--color-success-700)]">Yes</span>
          </label>
          <label className="flex items-center p-3 border border-[var(--color-neutral-border)] rounded-lg cursor-pointer hover:bg-[var(--color-neutral-50)] transition-colors bg-[var(--color-default-background)]">
            <input
              type="radio"
              name="vote"
              value="no"
              checked={selectedOption === 'no'}
              onChange={() => setSelectedOption('no')}
              className="mr-3"
              disabled={isCasting}
            />
            <span className="flex-1 font-medium text-[var(--color-error-700)]">No</span>
          </label>
          <label className="flex items-center p-3 border border-[var(--color-neutral-border)] rounded-lg cursor-pointer hover:bg-[var(--color-neutral-50)] transition-colors bg-[var(--color-default-background)]">
            <input
              type="radio"
              name="vote"
              value="abstain"
              checked={selectedOption === 'abstain'}
              onChange={() => setSelectedOption('abstain')}
              className="mr-3"
              disabled={isCasting}
            />
            <span className="flex-1 font-medium text-[var(--color-default-font)]">Abstain</span>
          </label>
        </div>
      </div>

      <Button
        type="submit"
        disabled={!selectedOption || isCasting}
        isLoading={isCasting}
        className="w-full"
      >
        {isCasting ? 'Casting Vote...' : 'Cast Vote'}
      </Button>

      {sessionType === 'anonymous' && (
        <p className="text-xs text-[var(--color-subtext-color)] text-center">
          Your vote will be anonymous
        </p>
      )}
    </form>
  );
};

export default VotingForm;

